import { createTheme, ThemeProvider } from "@mui/material/styles";
import React, {
  PropsWithChildren,
  createContext,
  useEffect,
  useState,
} from "react";

type ColorModeContextType = {
  mode: string;
  setMode: () => void;
};

export const ColorModeContext = createContext<ColorModeContextType>(
  {} as ColorModeContextType
);

const lightTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#3699FF",
      light: "#65B0FF",
      dark: "#006AE6",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#7239EA",
      light: "#8A56E2",
      dark: "#5A1FD1",
      contrastText: "#ffffff",
    },
    background: {
      default: "#F5F8FA",
      paper: "#FFFFFF",
    },
    divider: "rgba(0, 0, 0, 0.08)",
  },
  typography: {
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.03)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          fontWeight: 600,
        },
      },
    },
  },
});

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#3699FF",
      light: "#65B0FF",
      dark: "#006AE6",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#7239EA",
      light: "#8A56E2",
      dark: "#5A1FD1",
      contrastText: "#ffffff",
    },
    background: {
      default: "#000000",
      paper: "#000000",
    },
    text: {
      primary: "#FFFFFF",
      secondary: "rgba(255, 255, 255, 0.7)",
    },
    divider: "rgba(255, 255, 255, 0.08)",
  },
  typography: {
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: "0px 4px 25px rgba(54, 153, 255, 0.12)",
          border: "1px solid rgba(255, 255, 255, 0.15)",
          backgroundImage: "none",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          fontWeight: 600,
        },
      },
    },
  },
});

export const ColorModeContextProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  const savedPreference = localStorage.getItem("colorModePreference");
  const [preference, setPreference] = useState<"system" | "light" | "dark">(
    savedPreference === "light" || savedPreference === "dark"
      ? savedPreference
      : "system"
  );
  const [systemMode, setSystemMode] = useState<"light" | "dark">(
    mediaQuery.matches ? "dark" : "light"
  );
  const mode = preference === "system" ? systemMode : preference;

  useEffect(() => {
    const handleSystemThemeChange = (event: MediaQueryListEvent) => {
      setSystemMode(event.matches ? "dark" : "light");
    };
    mediaQuery.addEventListener("change", handleSystemThemeChange);
    return () => mediaQuery.removeEventListener("change", handleSystemThemeChange);
  }, []);

  useEffect(() => {
    const handleNativeSystemTheme = (event: Event) => {
      const requestedMode = (event as CustomEvent<{ mode?: string }>).detail?.mode;
      if (requestedMode !== "light" && requestedMode !== "dark") return;
      setPreference("system");
      setSystemMode(requestedMode);
    };
    window.addEventListener("collectohub:system-color-mode", handleNativeSystemTheme);
    return () =>
      window.removeEventListener("collectohub:system-color-mode", handleNativeSystemTheme);
  }, []);

  useEffect(() => {
    window.localStorage.setItem("colorMode", mode);
    window.localStorage.setItem("colorModePreference", preference);
    document.documentElement.style.colorScheme = mode;
    const bridge = (window as any).CollectoBridge;
    bridge?.emitNativeEvent?.("THEME_CHANGED", { mode, preference }, "LOG");
  }, [mode, preference]);

  const setColorMode = () => {
    setPreference(mode === "light" ? "dark" : "light");
  };

  const theme = mode === "light" ? lightTheme : darkTheme;

  return (
    <ColorModeContext.Provider
      value={{
        setMode: setColorMode,
        mode,
      }}
    >
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};
